// src/lib/firebase.js
/**
 * Firebase configuration and exports.
 * 
 * Provides:
 * - Email/password authentication 
 * - Phone number verification 
 * - reCAPTCHA verifier for SMS verification
 * - Firestore and Storage access
 * 
 * Security-first with mandatory email verification
 * and optional phone verification.
 */

import { initializeApp } from "firebase/app";
import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  sendEmailVerification,
  signInWithPopup,
  GoogleAuthProvider,
  PhoneAuthProvider,
  signInWithPhoneNumber,
  RecaptchaVerifier,
  connectAuthEmulator,
  fetchSignInMethodsForEmail,
} from "firebase/auth";
import {
  getFirestore,
  connectFirestoreEmulator,
  collection,
  query,
  where,
  getDocs,
  limit,
  doc,
  getDoc,
  setDoc,
} from "firebase/firestore";
import { getStorage } from "firebase/storage";
import { getFunctions } from "firebase/functions";
import { buildCanonicalUserDocument } from "./userSchema";
import { warnDev, errorDev, logDev } from "../utils/logger";

const defaultFirebaseConfig = {
  apiKey: "AIzaSyAEHXXWv5XKGXOUfck9QM0ad12noKXgExY",
  authDomain: "farmnet-v2-d8f2b.firebaseapp.com",
  projectId: "farmnet-v2-d8f2b",
  storageBucket: "farmnet-v2-d8f2b.appspot.com",
  messagingSenderId: "642941399018",
  appId: "1:642941399018:web:7b95d082908187e4eb0c93",
};

function getFirebaseConfig() {
  const envConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
  };

  const requiredKeys = Object.keys(defaultFirebaseConfig);
  const usingEnvConfig = requiredKeys.every((k) => typeof envConfig[k] === "string" && envConfig[k].trim().length > 0);

  if (usingEnvConfig) {
    return envConfig;
  }

  if (process.env.NODE_ENV !== "production") {
    warnDev("[Firebase] Using bundled Firebase config. Set REACT_APP_FIREBASE_* env vars for environment-specific deployments.");
  }

  return defaultFirebaseConfig;
}

const firebaseConfig = getFirebaseConfig();

// Initialize Firebase app once and only once!
const app = initializeApp(firebaseConfig);

// Export Firebase services **only after app is initialized**
export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);

// Development: Connect to emulators for local testing
if (process.env.NODE_ENV === "development" && process.env.REACT_APP_USE_FIREBASE_EMULATOR === "true") {
  try {
    // Emulator ports: Auth=9099, Firestore=8080
    // Start with: firebase emulators:start
    const auth = getAuth(app);
    connectAuthEmulator(auth, "http://localhost:9099", { disableWarnings: true });
    connectFirestoreEmulator(db, "localhost", 8080);
  } catch (error) {
    // Emulator already connected or not available - that's OK
    if (!error.message.includes("already connected")) {
      warnDev("[Firebase] Emulator connection failed (running against production):", error.message);
    }
  }
}

// Export app instance and functions client
export const functions = getFunctions(app);
export { app };

/**
 * Setup reCAPTCHA verifier for SMS verification .
 * Call once when component mounts that handles phone verification.
 * 
 * @param {string} containerId - DOM element ID for reCAPTCHA (usually "recaptcha-container")
 * @returns {RecaptchaVerifier} verifier instance
 */
export function setupRecaptchaVerifier(containerId = "recaptcha-container") {
  try {
    // Check if element exists
    const container = document.getElementById(containerId);
    if (!container) {
      warnDev(`[reCAPTCHA] Container element #${containerId} not found`);
      return null;
    }
    
    const verifier = new RecaptchaVerifier(auth, containerId, {
      size: "invisible",
      callback: (token) => {
        logDev("[reCAPTCHA] Token received");
      },
      "expired-callback": () => {
        logDev("[reCAPTCHA] Token expired");
      },
    });
    return verifier;
  } catch (error) {
    errorDev("[reCAPTCHA] Setup failed:", error);
    return null;
  }
}

/**
 * Sign in with email and password.
 * 
 * @param {string} email
 * @param {string} password
 * @returns {Promise<UserCredential>}
 */
export async function signInWithEmail(email, password) {
  return signInWithEmailAndPassword(auth, email, password);
}

/**
 * Create user with email and password.
 * User is NOT verified by default - email verification required.
 * 
 * @param {string} email
 * @param {string} password
 * @returns {Promise<UserCredential>}
 */
export async function createUserWithEmail(email, password) {
  return createUserWithEmailAndPassword(auth, email, password);
}

/**
 * Send email verification to user.
 * 
 * @param {User} user - Firebase user object
 * @returns {Promise<void>}
 */
export async function sendVerificationEmail(user) {
  const continueUrl = `${window.location.origin}/verify-email-action`;
  return sendEmailVerification(user, {
    url: continueUrl,
    handleCodeInApp: true,
  });
}

/**
 * Send password reset email.
 * 
 * @param {string} email
 * @returns {Promise<void>}
 */
export async function resetPassword(email) {
  return sendPasswordResetEmail(auth, email);
}

/**
 * Sign out current user.
 * 
 * @returns {Promise<void>}
 */
export async function signOutUser() {
  return signOut(auth);
}

/**
 * Google sign-in (if configured).
 * Provider must be enabled in Firebase console.
 * 
 * @returns {Promise<UserCredential>}
 */
export async function signInWithGoogle() {
  const provider = new GoogleAuthProvider();
  return signInWithPopup(auth, provider);
}

/**
 * Ensure the Firestore profile document exists for any authenticated user.
 * This is idempotent and safe to call from all auth entry points.
 *
 * @param {import("firebase/auth").User} firebaseUser
 * @param {{ markProfileComplete?: boolean, profileData?: Record<string, any> }} options
 * @returns {Promise<void>}
 */
export async function ensureUserProfileDocument(firebaseUser, options = {}) {
  if (!firebaseUser?.uid) return;

  const userRef = doc(db, "users", firebaseUser.uid);
  const existing = await getDoc(userRef);

  const existingData = existing.exists() ? existing.data() : {};
  const patch = buildCanonicalUserDocument({
    firebaseUser,
    existingData,
    profileData: options.profileData || {},
    markProfileComplete: Boolean(options.markProfileComplete),
    now: new Date(),
  });

  await setDoc(userRef, patch, { merge: true });
}

/**
 * Sign in with phone number 
 * Uses reCAPTCHA verifier 
 * 
 * @param {string} phoneNumber - F
 * @param {RecaptchaVerifier} appVerifier - reCAPTCHA verifier instance
 * @returns {Promise<ConfirmationResult>}
 */
export async function signInWithPhoneAuth(phoneNumber, appVerifier) {
  return signInWithPhoneNumber(auth, phoneNumber, appVerifier);
}

/**
 * Check if email is available for signup.
 * CORRECT METHOD: Uses fetchSignInMethodsForEmail instead of failed login attempts.
 * 
 * @param {string} email - Email to check
 * @returns {Promise<boolean>} true if email is available, false if already registered
 * @throws {Error} If email check fails or email format is invalid
 */
export async function isEmailAvailable(email) {
  try {
    // This API returns all sign-in methods for the email
    // Empty array = email not registered anywhere
    const methods = await fetchSignInMethodsForEmail(auth, email);
    const available = methods.length === 0;
    if (process.env.NODE_ENV !== "production") {
      logDev("[isEmailAvailable] Email check result", {
        available,
        methodsFound: methods.length,
      });
    }
    
    return available;
  } catch (error) {
    if (process.env.NODE_ENV !== "production") {
      warnDev("[isEmailAvailable] Email check failed", {
        code: error.code,
        message: error.message,
      });
    }
    
    // Throw error to let Step 1 validation handle it
    // This prevents blind signup when the check fails
    if (error.code === "auth/invalid-email") {
      throw new Error("Email format is invalid. Please check and try again.");
    }
    
    throw new Error(
      "Could not verify email availability. Please check your internet connection and try again."
    );
  }
}

/**
 * Feature flag: Check if phone verification is enabled.
 * Phone Auth (OTP) is not yet supported in the signup flow.
 * Registration stays a simple 2-step flow (account → profile) unless
 * explicitly enabled via REACT_APP_ENABLE_PHONE_AUTH=true.
 *
 * Default: disabled everywhere (set REACT_APP_ENABLE_PHONE_AUTH=true to enable)
 * @returns {boolean} true if phone verification is enabled
 */
export function isPhoneAuthEnabled() {
  const envFlag = process.env.REACT_APP_ENABLE_PHONE_AUTH;

  // If explicitly set, use that value
  if (envFlag === "true") return true;
  if (envFlag === "false") return false;

  // Default: disabled — OTP phone verification is not yet supported
  return false;
}

/**
 * Phone auth provider for linking phone to existing account.
 * 
 * @returns {PhoneAuthProvider}
 */
export function getPhoneAuthProvider() {
  return new PhoneAuthProvider(auth);
}

/**
 * Listen to auth state changes.
 * 
 * @param {Function} callback
 * @returns {Function} unsubscribe function
 */
export function onAuthStateChangedListener(callback) {
  return onAuthStateChanged(auth, callback);
}

/**
 * Find a user's email by their username/displayName.
 * Queries Firestore `users` collection by `displayName` and `name` fields.
 * Used for username + password login (resolves username → email → Firebase Auth).
 *
 * @param {string} username - The username to look up
 * @returns {Promise<string|null>} The user's email, or null if not found
 */
export async function findEmailByUsername(username) {
  if (!username?.trim()) return null;
  const trimmed = username.trim();
  const normalized = trimmed.toLowerCase();
  const usersRef = collection(db, "users");

  const q0 = query(usersRef, where("usernameLower", "==", normalized), limit(1));
  const snap0 = await getDocs(q0);
  if (!snap0.empty) {
    const data = snap0.docs[0].data();
    if (data.email) return data.email;
  }

  const q00 = query(usersRef, where("username", "==", trimmed), limit(1));
  const snap00 = await getDocs(q00);
  if (!snap00.empty) {
    const data = snap00.docs[0].data();
    if (data.email) return data.email;
  }

  // Try displayName match (exact)
  const q1 = query(usersRef, where("displayName", "==", trimmed), limit(1));
  const snap1 = await getDocs(q1);
  if (!snap1.empty) {
    const data = snap1.docs[0].data();
    if (data.email) return data.email;
  }

  // Try name field
  const q2 = query(usersRef, where("name", "==", trimmed), limit(1));
  const snap2 = await getDocs(q2);
  if (!snap2.empty) {
    const data = snap2.docs[0].data();
    if (data.email) return data.email;
  }

  return null;
}

/**
 * Find a user's email by their phone number.
 * Queries Firestore `users` collection by `phone` and `phoneNumber` fields.
 * Used for phone + password login (resolves phone → email → Firebase Auth).
 *
 * @param {string} phone - The phone number to look up (with or without spaces)
 * @returns {Promise<string|null>} The user's email, or null if not found
 */
export async function findEmailByPhone(phone) {
  if (!phone?.trim()) return null;
  const normalized = phone.replace(/[\s\-()]/g, ""); // strip spaces, dashes, parens
  const usersRef = collection(db, "users");

  // Try phone field
  const q1 = query(usersRef, where("phone", "==", normalized), limit(1));
  const snap1 = await getDocs(q1);
  if (!snap1.empty) {
    const data = snap1.docs[0].data();
    if (data.email) return data.email;
  }

  // Try phoneNumber field
  const q2 = query(usersRef, where("phoneNumber", "==", normalized), limit(1));
  const snap2 = await getDocs(q2);
  if (!snap2.empty) {
    const data = snap2.docs[0].data();
    if (data.email) return data.email;
  }

  return null;
}

/**
 * Check if a newly signed-up email has a pre-provisioned admin role.
 * If yes, apply it to the user's Firestore document and mark as applied.
 * Called automatically during signup.
 *
 * @param {string} uid - Firebase Auth UID
 * @param {string} email - User's email address
 * @returns {string|null} - Applied role or null
 */
export async function checkAndApplyPendingRole(uid, email) {
  if (!uid || !email) return null;
  try {
    const normalized = email.trim().toLowerCase();
    const pendingRef = doc(db, "pending_admin_roles", normalized);
    const { getDoc: fetchDoc, updateDoc } = await import("firebase/firestore");
    const directSnap = await fetchDoc(pendingRef);

    if (!directSnap.exists()) return null;
    const pendingData = directSnap.data();
    if (!pendingData || !pendingData.role) return null;
    if (pendingData.applied) return pendingData.role; // Already applied

    const role = pendingData.role;

    // Apply role to user document (merge so it works even if doc doesn't exist)
    const userRef = doc(db, "users", uid);
    await setDoc(userRef, {
      role,
      roleUpdatedAt: new Date(),
      roleUpdatedBy: "auto-provision",
    }, { merge: true });

    // Mark pending as applied (best-effort, may fail if rules restrict)
    try {
      await updateDoc(pendingRef, {
        applied: true,
        appliedAt: new Date(),
        appliedToUid: uid,
      });
    } catch (err) {
      // Non-critical — the role is already applied to the user
    errorDev("Operation failed:", err);
    }

    return role;
  } catch (err) {
    // Silent fail — don't block signup for role assignment
    return null;
  }
}

